import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { FiCalendar, FiChevronDown, FiRefreshCw } from "react-icons/fi";
import { HiOutlineBanknotes } from "react-icons/hi2";
import { TbReceipt2 } from "react-icons/tb";
import { MdOutlineBalance } from "react-icons/md";
import { BsCashStack } from "react-icons/bs";

import "./AccountingPage.css";

/* Chart palette */
const COLORS = [
  "#3B82F6",
  "#F43F5E",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#06B6D4",
  "#EF4444",
  "#84CC16",
  "#A78BFA",
  "#14B8A6",
  "#FB7185",
  "#EAB308",
];

export default function AccountingPage() {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState([]); // [{category,total,type}]
  const [loading, setLoading] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [accountId, setAccountId] = useState("");

  // Popover open state (click-to-open, click-away to close)
  const [dateOpen, setDateOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const dateRef = useRef(null);
  const acctRef = useRef(null);
  const cashRef = useRef(null);
  const [balances, setBalances] = useState([]);
  const [showCashHover, setShowCashHover] = useState(false);
  const [refreshingBalances, setRefreshingBalances] = useState(false);

  // Close popovers on outside click or Esc
 useEffect(() => {
  const onDocClick = (e) => {
    if (dateOpen && dateRef.current && !dateRef.current.contains(e.target)) {
      setDateOpen(false);
    }

    if (acctOpen && acctRef.current && !acctRef.current.contains(e.target)) {
      setAcctOpen(false);
    }

    if (
      showCashHover &&
      cashRef.current &&
      !cashRef.current.contains(e.target)
    ) {
      setShowCashHover(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Escape") {
      setDateOpen(false);
      setAcctOpen(false);
      setShowCashHover(false);
    }
  };

  document.addEventListener("mousedown", onDocClick);
  document.addEventListener("keydown", onKey);

  return () => {
    document.removeEventListener("mousedown", onDocClick);
    document.removeEventListener("keydown", onKey);
  };
}, [dateOpen, acctOpen, showCashHover]);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await api.get(`/plaid/accounts/${businessId}`);
      setAccounts(res.data || []);
    } catch (err) {
      console.error("Accounts fetch failed:", err);
      setAccounts([]);
    }
  }, [businessId]);

  const fetchBalances = useCallback(async () => {
  try {
    const res = await api.get(`/plaid/fetch-balances/${businessId}`);
    setBalances(res.data || []);
  } catch (err) {
    console.error("Balance fetch failed:", err);
    setBalances([]);
  }
}, [businessId]);


const refreshBalances = useCallback(async () => {
  try {
    setRefreshingBalances(true);

    const res = await api.post("/plaid/refresh-balances");

    if (res.data?.cooldown) {
      alert(
        `Balances were refreshed recently. Please wait ${res.data.minutes_remaining} minute(s) before refreshing again.`
      );
      return;
    }

    await fetchBalances();
    setShowCashHover(true);
  } catch (err) {
    console.error("Refresh balances failed:", err);
    alert("Failed to refresh balances.");
  } finally {
    setRefreshingBalances(false);
  }
}, [fetchBalances]);

  // Fetch summary (called on mount and when pressing Apply)
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/plaid/transactions/${businessId}/category-summary`,
        {
          params: {
            start_date: startDate,
            end_date: endDate,
            account_id: accountId || undefined,
          },
        }
      );
      setSummary(res.data?.summary || []);
    } catch (err) {
      console.error("Summary fetch failed:", err);
      setSummary([]);
    } finally {
      setLoading(false);
    }
  }, [businessId, startDate, endDate, accountId]);

  useEffect(() => {
  fetchAccounts();
  fetchBalances();
}, [fetchAccounts, fetchBalances]);

useEffect(() => {
  fetchSummary();
}, [fetchSummary]);

  /* Build datasets */
  const income = useMemo(
    () =>
      summary
        .filter((r) => r.type === "income")
        .map((r) => ({
          name: pretty(r.category),
          value: Math.abs(Number(r.total || 0)),
        }))
        .filter((d) => d.value > 0),
    [summary]
  );

  const expenses = useMemo(
    () =>
      summary
        .filter((r) => r.type === "expense")
        .map((r) => ({ name: pretty(r.category), value: Number(r.total || 0) }))
        .filter((d) => d.value > 0),
    [summary]
  );

  const totalIncome = income.reduce((s, d) => s + d.value, 0);
  const totalExpenses = expenses.reduce((s, d) => s + d.value, 0);
  const netBalance = totalIncome - totalExpenses;

  const cashOnHand = useMemo(
  () =>
    balances.reduce((sum, a) => {
      const v = Number(a.available ?? a.current ?? 0);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0),
  [balances]
);

  return (
    <div className="accounting-page">
      {/* HEADER */}
      <div className="ap-head">
      
        <h3 className="pw-page-title">Accounting</h3>
        

        <div className="ap-pills">
          {/* Date pill */}
          <div
            className={`pill ${dateOpen ? "is-open" : ""}`}
            ref={dateRef}
            role="button"
            tabIndex={0}
            onClick={() => setDateOpen((v) => !v)}
            onKeyDown={(e) => e.key === "Enter" && setDateOpen((v) => !v)}
            aria-haspopup="dialog"
            aria-expanded={dateOpen}
          >
            <FiCalendar />
            <span>Date</span>
            <FiChevronDown />
            <div
              className="pill-pop"
              style={{ display: dateOpen ? "grid" : "none" }}
              onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => e.stopPropagation()}
            >
              <label>
                Start
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <label>
                End
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </label>
              <button
                onClick={() => {
                  fetchSummary();
                  setDateOpen(false);
                }}
                disabled={loading}
              >
                {loading ? "Applying..." : "Apply"}
              </button>
            </div>
          </div>

          {/* Account pill */}
          <div
            className={`pill ${acctOpen ? "is-open" : ""}`}
            ref={acctRef}
            role="button"
            tabIndex={0}
            onClick={() => setAcctOpen((v) => !v)}
            onKeyDown={(e) => e.key === "Enter" && setAcctOpen((v) => !v)}
            aria-haspopup="dialog"
            aria-expanded={acctOpen}
          >
            <BsCashStack />
            <span>Account</span>
            <FiChevronDown />
            <div
              className="pill-pop"
              style={{ display: acctOpen ? "grid" : "none" }}
              onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => e.stopPropagation()}
            >
              <label>Account</label>
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                <option value="">All</option>
                {accounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {(a.bank_name || a.name || "Account")}
                    {a.mask ? ` •${a.mask}` : ""}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  fetchSummary();
                  setAcctOpen(false);
                }}
                disabled={loading}
              >
                {loading ? "Applying..." : "Apply"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="ap-kpis">
        <KPI
          icon={<HiOutlineBanknotes />}
          label="Total Income"
          sub="This Period"
          value={toMoney(totalIncome)}
        />
        <KPI
          icon={<TbReceipt2 />}
          label="Total Expenses"
          sub="This Period"
          value={toMoney(totalExpenses)}
        />
        <KPI
          icon={<MdOutlineBalance />}
          label="Net Balance"
          sub="Income – Expenses"
          value={toMoney(netBalance)}
        />
        <div
  className="cash-kpi-wrap"
  ref={cashRef}
  onMouseEnter={() => {
    if (window.innerWidth > 768) {
      setShowCashHover(true);
    }
  }}
  onMouseLeave={(e) => {
    const nextTarget = e.relatedTarget;

    if (
      !nextTarget ||
      !(nextTarget instanceof Node) ||
      !cashRef.current?.contains(nextTarget)
    ) {
      setShowCashHover(false);
    }
  }}
>
  <button
    type="button"
    className="cash-refresh-btn"
    onClick={async (e) => {
      e.stopPropagation();
      await refreshBalances();
    }}
    disabled={refreshingBalances}
    title="Refresh balances"
  >
    <FiRefreshCw className={refreshingBalances ? "spin" : ""} />
  </button>

  <div
    className="cash-kpi-trigger"
    role="button"
    tabIndex={0}
    onClick={() => setShowCashHover((prev) => !prev)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setShowCashHover((prev) => !prev);
      }
    }}
  >
    <KPI
      icon={<BsCashStack />}
      label="Cash on Hand"
      sub="Linked Accounts"
      value={toMoney(cashOnHand)}
    />
  </div>

  {showCashHover && (
    <div
      className="cash-hover-pop"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {balances.length === 0 ? (
        <div className="cash-hover-empty">No balances available</div>
      ) : (
        balances.map((a) => {
          const balance = Number(a.available ?? a.current ?? 0);

          return (
            <div className="cash-hover-row" key={a.account_id}>
              <div className="cash-hover-left">
                <div className="cash-hover-name">
                  {a.name || "Account"}
                  {a.mask ? ` •${a.mask}` : ""}
                </div>

                {a.type || a.subtype ? (
                  <div className="cash-hover-sub">
                    {[a.type, a.subtype].filter(Boolean).join(" • ")}
                  </div>
                ) : null}
              </div>

              <div className="cash-hover-balance">
                {toMoney(balance)}
              </div>
            </div>
          );
        })
      )}
    </div>
  )}
</div>
      </div>

      {/* CHARTS */}
      <div className="ap-charts">
        {/* Overview */}
        <div className="chart-card">
          <div className="chart-title">Financial Overview</div>
          <div className="donut">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: "Income", value: totalIncome },
                    { name: "Expenses", value: totalExpenses },
                  ]}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  stroke="none"
                >
                  <Cell fill="#3B82F6" />
                  <Cell fill="#F43F5E" />
                </Pie>
                <Tooltip formatter={(v) => toMoney(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="legend-inline">
            <span className="dot" style={{ background: "#3B82F6" }} />
            Income
            <span className="dot" style={{ background: "#F43F5E" }} />
            Expenses
          </div>
        </div>

        {/* Expense Breakdown */}
        <div className="chart-card">
          <div className="chart-title">Expense Breakdown</div>
          <div className="chart-split">
            <div className="donut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={expenses.length ? expenses : [{ name: "No Data", value: 1 }]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={110}
                    stroke="none"
                  >
                    {(expenses.length ? expenses : [{ name: "No Data", value: 1 }]).map(
                      (e, i) => (
                        <Cell
                          key={e.name + i}
                          fill={COLORS[i % COLORS.length]}
                        />
                      )
                    )}
                  </Pie>
                  <Tooltip
  formatter={(value, name) => {
    const amount = Number(value) || 0;
    const pctOfIncome =
      totalIncome > 0 ? ((amount / totalIncome) * 100).toFixed(2) : "0.00";

    return [`${toMoney(amount)} • ${pctOfIncome}% of total income`, name];
  }}
/>
                </PieChart>
              </ResponsiveContainer>
            </div>

            <ul className="legend-list">
  {(expenses.length ? expenses : [{ name: "No Data", value: 0 }])
    .slice(0, 12)
    .map((e, i) => {
      const pctOfIncome =
        totalIncome > 0 ? ((Number(e.value || 0) / totalIncome) * 100).toFixed(2) : "0.00";

      return (
        <li key={e.name + i}>
          <span
            className="dot"
            style={{ background: COLORS[i % COLORS.length] }}
          />
          {e.name} {e.name !== "No Data" ? `(${pctOfIncome}%)` : ""}
        </li>
      );
    })}
</ul>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="ap-actions">
        <ActionCard
          title="Manage Accounts"
          desc="Add or update linked accounts."
          cta="Bank Accounts"
          onClick={() =>
            navigate(`/dashboard/business/${businessId}/banking`)
          }
        />
        <ActionCard
          title="Transactions"
          desc="Review all activity in one place."
          cta="View Details"
          onClick={() =>
            navigate(`/dashboard/business/${businessId}/transactions-view`)
          }
        />
        <ActionCard
          title="Cash In Reconciliation"
          desc="Match payroll with expenses."
          cta="Start Check"
          onClick={() =>
            navigate(
              `/dashboard/business/${businessId}/cash-recon`
            )
          }
        />
        <ActionCard
          title="Integrations"
          desc="Verify incoming cash flow."
          cta="Review Now"
          onClick={() =>
            navigate(`/dashboard/business/${businessId}/integration`)
          }
        />
      </div>
    </div>
  );
}

/* Helpers / tiny components /dashboard/business/${businessId}/payroll-reconciliation*/
function KPI({ icon, label, sub, value }) {
  return (
    <div className="kpi">
      <div className="kpi-icon">{icon}</div>
      <div className="kpi-body">
        <div className="kpi-value">{value}</div>
        <div className="kpi-label">{label}</div>
        <div className="kpi-sub">{sub}</div>
      </div>
    </div>
  );
}

function ActionCard({ title, desc, cta, onClick }) {
  return (
    <div className="action-card">
      <div className="action-left">
        <div className="action-title">{title}</div>
        <div className="action-desc">{desc}</div>
      </div>
      <button className="action-cta" onClick={onClick}>
        {cta} ▸
      </button>
    </div>
  );
}

function pretty(s = "") {
  return s
    .toString()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
function toMoney(v = 0) {
  const n = Number(v) || 0;
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}





// import React, { useEffect, useMemo, useState, useCallback } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import api from "../services/api";
// import {
//   PieChart,
//   Pie,
//   Cell,
//   Tooltip,
//   ResponsiveContainer,
// } from "recharts";

// import { FiCalendar, FiChevronDown } from "react-icons/fi";
// import { HiOutlineBanknotes } from "react-icons/hi2";
// import { TbReceipt2 } from "react-icons/tb";
// import { MdOutlineBalance } from "react-icons/md";
// import { BsCashStack } from "react-icons/bs";

// import "./AccountingPage.css";

// /** Color palette for charts/legend (pleasant, accessible) */
// const COLORS = [
//   "#3B82F6", "#F43F5E", "#10B981", "#F59E0B",
//   "#8B5CF6", "#06B6D4", "#EF4444", "#84CC16",
//   "#A78BFA", "#14B8A6", "#FB7185", "#EAB308"
// ];

// export default function AccountingPage() {
//   const { id: businessId } = useParams();
//   const navigate = useNavigate();

//   const [accounts, setAccounts] = useState([]);
//   const [summary, setSummary]   = useState([]); // [{category,total,type}]
//   const [loading, setLoading]   = useState(false);

//   // Filter state (matches “Date” & “Account” pills)
//   const [startDate, setStartDate] = useState(() => {
//     const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10);
//   });
//   const [endDate, setEndDate]     = useState(() => new Date().toISOString().slice(0, 10));
//   const [accountId, setAccountId] = useState("");

//   /** Fetch linked accounts (for cash on hand + account filter) */
//   const fetchAccounts = useCallback(async () => {
//     try {
//       const res = await api.get(`/plaid/accounts/${businessId}`);
//       setAccounts(res.data || []);
//     } catch {
//       setAccounts([]);
//     }
//   }, [businessId]);

//   /** Fetch category summary used for KPIs + charts */
//   const fetchSummary = useCallback(async () => {
//     setLoading(true);
//     try {
//       const res = await api.get(
//         `/plaid/transactions/${businessId}/category-summary`,
//         { params: { start_date: startDate, end_date: endDate, account_id: accountId || undefined } }
//       );
//       setSummary(res.data?.summary || []);
//     } catch {
//       setSummary([]);
//     } finally {
//       setLoading(false);
//     }
//   }, [businessId, startDate, endDate, accountId]);

//   useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
//   useEffect(() => { fetchSummary(); }, [fetchSummary]);

//   /** Build dataset for the two pies and top KPIs */
//   const income = useMemo(
//     () =>
//       summary
//         .filter(r => r.type === "income")
//         .map(r => ({ name: pretty(r.category), value: Math.abs(Number(r.total || 0)) }))
//         .filter(d => d.value > 0),
//     [summary]
//   );

//   const expenses = useMemo(
//     () =>
//       summary
//         .filter(r => r.type === "expense")
//         .map(r => ({ name: pretty(r.category), value: Number(r.total || 0) }))
//         .filter(d => d.value > 0),
//     [summary]
//   );

//   const totalIncome   = income.reduce((s, d) => s + d.value, 0);
//   const totalExpenses = expenses.reduce((s, d) => s + d.value, 0);
//   const netBalance    = totalIncome - totalExpenses;

//   /** Cash on hand = sum of available/current across accounts */
//   const cashOnHand = useMemo(
//     () => accounts.reduce((sum, a) => {
//       const v = Number(a.available ?? a.current ?? 0);
//       return sum + (Number.isFinite(v) ? v : 0);
//     }, 0),
//     [accounts]
//   );

//   return (
//     <div className="accounting-page">
//       {/* HEADER */}
//       <div className="ap-head">
//         <h1>Accounting</h1>

//         <div className="ap-pills">
//           {/* Date pill */}
//           <div className="pill">
//             <FiCalendar />
//             <span>Date</span>
//             <FiChevronDown />
//             <div className="pill-pop">
//               <label>
//                 Start
//                 <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
//               </label>
//               <label>
//                 End
//                 <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
//               </label>
//               <button onClick={fetchSummary} disabled={loading}>Apply</button>
//             </div>
//           </div>

//           {/* Account pill */}
//           <div className="pill">
//             <BsCashStack />
//             <span>Account</span>
//             <FiChevronDown />
//             <div className="pill-pop">
//               <label>Account</label>
//               <select value={accountId} onChange={e => setAccountId(e.target.value)}>
//                 <option value="">All</option>
//                 {accounts.map(a => (
//                   <option key={a.account_id} value={a.account_id}>
//                     {(a.bank_name || a.name || "Account")}{a.mask ? ` •${a.mask}` : ""}
//                   </option>
//                 ))}
//               </select>
//               <button onClick={fetchSummary} disabled={loading}>Apply</button>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* KPI STRIP */}
//       <div className="ap-kpis">
//         <KPI
//           icon={<HiOutlineBanknotes />}
//           label="Total Income"
//           sub="This Month"
//           value={toMoney(totalIncome)}
//         />
//         <KPI
//           icon={<TbReceipt2 />}
//           label="Total Expenses"
//           sub="This Month"
//           value={toMoney(totalExpenses)}
//         />
//         <KPI
//           icon={<MdOutlineBalance />}
//           label="Net Balance"
//           sub="Income – Expenses"
//           value={toMoney(netBalance)}
//         />
//         <KPI
//           icon={<BsCashStack />}
//           label="Cash on Hand"
//           sub="Liquidity available"
//           value={toMoney(cashOnHand)}
//         />
//       </div>

//       {/* CHART ROW */}
//       <div className="ap-charts">
//         {/* Left: Financial Overview (Income vs Expenses) */}
//         <div className="chart-card">
//           <div className="chart-title">Financial Overview</div>
//           <div className="donut">
//             <ResponsiveContainer width="100%" height="100%">
//               <PieChart>
//                 <Pie
//                   data={[
//                     { name: "Income", value: totalIncome },
//                     { name: "Expenses", value: totalExpenses },
//                   ]}
//                   dataKey="value"
//                   cx="50%"
//                   cy="50%"
//                   innerRadius={70}
//                   outerRadius={110}
//                   stroke="none"
//                 >
//                   <Cell fill="#3B82F6" />
//                   <Cell fill="#F43F5E" />
//                 </Pie>
//                 <Tooltip formatter={(v) => toMoney(v)} />
//               </PieChart>
//             </ResponsiveContainer>
//           </div>

//           <div className="legend-inline">
//             <span className="dot" style={{ background: "#3B82F6" }}></span> Income
//             <span className="dot" style={{ background: "#F43F5E" }}></span> Expenses
//           </div>
//         </div>

//         {/* Right: Expense Breakdown with legend list */}
//         <div className="chart-card">
//           <div className="chart-title">Expense Breakdown</div>

//           <div className="chart-split">
//             <div className="donut">
//               <ResponsiveContainer width="100%" height="100%">
//                 <PieChart>
//                   <Pie
//                     data={expenses.length ? expenses : [{ name: "No Data", value: 1 }]}
//                     dataKey="value"
//                     nameKey="name"
//                     cx="50%"
//                     cy="50%"
//                     innerRadius={70}
//                     outerRadius={110}
//                     stroke="none"
//                   >
//                     {(expenses.length ? expenses : [{ name: "No Data", value: 1 }]).map((e, i) => (
//                       <Cell key={e.name + i} fill={COLORS[i % COLORS.length]} />
//                     ))}
//                   </Pie>
//                   <Tooltip formatter={(v) => toMoney(v)} />
//                 </PieChart>
//               </ResponsiveContainer>
//             </div>

//             <ul className="legend-list">
//               {(expenses.length ? expenses : [{ name: "No Data" }]).slice(0, 12).map((e, i) => (
//                 <li key={e.name + i}>
//                   <span className="dot" style={{ background: COLORS[i % COLORS.length] }} />
//                   {e.name}
//                 </li>
//               ))}
//             </ul>
//           </div>
//         </div>
//       </div>

//       {/* ACTION CARDS */}
//       <div className="ap-actions">
//         <ActionCard
//           title="Manage Accounts"
//           desc="Add or update linked accounts."
//           cta="Open account"
//           onClick={() => navigate(`/dashboard/business/${businessId}/banking`)}
//         />
//         <ActionCard
//           title="Transactions"
//           desc="Review all activity in one place."
//           cta="View Details"
//           onClick={() => navigate(`/dashboard/business/${businessId}/transactions-view`)}
//         />
//         <ActionCard
//           title="Payroll Reconciliation"
//           desc="Match payroll with expenses."
//           cta="Start Check"
//           onClick={() => navigate(`/dashboard/business/${businessId}/payroll-reconciliation`)}
//         />
//         <ActionCard
//           title="Cash-In Reconciliation"
//           desc="Verify incoming cash flow."
//           cta="Review Now"
//           onClick={() => navigate(`/dashboard/business/${businessId}/cash-recon`)}
//         />
//       </div>
//     </div>
//   );
// }

// /* ——— small helpers/components ——— */

// function KPI({ icon, label, sub, value }) {
//   return (
//     <div className="kpi">
//       <div className="kpi-icon">{icon}</div>
//       <div className="kpi-body">
//         <div className="kpi-value">{value}</div>
//         <div className="kpi-label">{label}</div>
//         <div className="kpi-sub">{sub}</div>
//       </div>
//     </div>
//   );
// }

// function ActionCard({ title, desc, cta, onClick }) {
//   return (
//     <div className="action-card">
//       <div className="action-left">
//         <div className="action-title">{title}</div>
//         <div className="action-desc">{desc}</div>
//       </div>
//       <button className="action-cta" onClick={onClick}>
//         {cta} ▸
//       </button>
//     </div>
//   );
// }

// function pretty(s = "") {
//   return s.toString().toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
// }
// function toMoney(v = 0) {
//   const n = Number(v) || 0;
//   return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
// }


// import React, { useState, useEffect, useCallback } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import api from "../services/api";
// import "./AccountingPage.css";
// import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
// import {
//   FaSyncAlt,
// } from "react-icons/fa";

// const AccountingPage = () => {
//   const { id: businessId } = useParams();
//   const navigate = useNavigate();
//   const [accounts, setAccounts] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [showAddCategoryPopup, setShowAddCategoryPopup] = useState(false);
//   const [showEditCategoryPopup, setShowEditCategoryPopup] = useState(false);
//   const [newCategoryName, setNewCategoryName] = useState("");
//   const [newCategoryType, setNewCategoryType] = useState("");
//   const [selectedCategoryId, setSelectedCategoryId] = useState("");
//   const [editedName, setEditedName] = useState("");
//   const [editedType, setEditedType] = useState("Expense");
//   const [plAccountId, setPlAccountId] = useState(""); // '' = All
//   const [plSummary, setPlSummary] = useState([]);     // [{category,total,type}]
//   const [loadingPie, setLoadingPie] = useState(false);
//   const [pieError, setPieError] = useState("");
//   // NEW: Cash Account popup state
//   const [showAddCashPopup, setShowAddCashPopup] = useState(false); // ← NEW
//   const [cashAccountName, setCashAccountName] = useState("Cash");  // ← NEW
//   const [cashSubtype, setCashSubtype] = useState("cash");          // ← NEW
//   const [creatingCash, setCreatingCash] = useState(false);         // ← NEW


//   const [plStartDate, setPlStartDate] = useState(() => {
//   const d = new Date();
//   d.setDate(1); // first of current month
//   return d.toISOString().slice(0, 10);
// });
// const [plEndDate, setPlEndDate] = useState(() => {
//   const d = new Date();
//   return d.toISOString().slice(0, 10);
// });

//   const fetchAccounts = useCallback(async () => {
//     try {
//       const res = await api.get(`/plaid/accounts/${businessId}`);
//       setAccounts(res.data || []);
//     } catch (err) {
//       console.error("Error fetching accounts:", err);
//     }
//   }, [businessId]);
  
//   const fetchCategories = useCallback(async () => {
//     try {
//       const res = await api.get("/categories/by-id", {
//         params: { business_id: businessId },
//       });
//       setCategories(res.data);
//     } catch (err) {
//       console.error("Error fetching categories", err);
//     }
//   }, [businessId]);

//   useEffect(() => {
//     fetchAccounts();
//     fetchCategories();
//   }, [fetchAccounts, fetchCategories]);

  


//   const COLORS = [
//   "#0088FE", "#00C49F", "#FFBB28", "#FF8042",
//   "#A28BFE", "#FF66C3", "#33CC99", "#FF4444"
// ];

// const colorFor = (str, idx) => COLORS[idx % COLORS.length];

  

 

//   const handleAddCategory = async () => {
//     if (!newCategoryName.trim()) return alert("Category name required");
//     await api.post("/plaid-categories", {
//       name: newCategoryName,
//       category_type: newCategoryType,
//       business_id: businessId,
//     });
//     alert("Category added");
//     setNewCategoryName("");
//     setNewCategoryType("");
//     setShowAddCategoryPopup(false);
//     fetchCategories();
//   };

//   const fetchProfitLossSummary = useCallback(async () => {
//   if (!plStartDate || !plEndDate) return;
//   setLoadingPie(true);
//   setPieError("");
//   try {
//     const res = await api.get(`/plaid/transactions/${businessId}/category-summary`, {
//       params: {
//         start_date: plStartDate,
//         end_date: plEndDate,
//         account_id: plAccountId || undefined,
//       },
//     });
//     setPlSummary(res.data?.summary || []);
//   } catch (err) {
//     console.error("PL summary error", err);
//     setPieError("Could not load category summary.");
//   } finally {
//     setLoadingPie(false);
//   }
// }, [businessId, plStartDate, plEndDate, plAccountId]);

// useEffect(() => {
//   fetchProfitLossSummary();
// }, [fetchProfitLossSummary]);

// const formatCategory = (cat = "") =>
//   cat
//     .toLowerCase()
//     .replace(/_/g, " ")
//     .replace(/\b\w/g, (c) => c.toUpperCase());

// const incomeData = plSummary
//   .filter(r => r.type === "income")
//   .map(r => ({
//     name: formatCategory(r.category || ""),
//     value: Number(r.total) > 0 ? Number(r.total) : Math.abs(Number(r.total)) // income is usually negative in your system, so flip to positive
//   }))
//   .filter(d => d.value > 0);

// const expenseData = plSummary
//   .filter(r => r.type === "expense" && Number(r.total) > 0) // exclude negatives
//   .map(r => ({
//     name: formatCategory(r.category || ""),
//     value: Number(r.total)
//   }));


// const totalIncomeBase = incomeData.reduce((s, d) => s + d.value, 0) || 0;




//   const handleEditCategory = async () => {
//     if (!selectedCategoryId || !editedName.trim()) return;
//     await api.put("/categories", {
//       id: selectedCategoryId,
//       name: editedName,
//       type: editedType,
//     });
//     alert("Category updated");
//     setShowEditCategoryPopup(false);
//     fetchCategories();
//   };

//   const handleDeleteCategory = async () => {
//     if (!selectedCategoryId) return;
//     await api.delete(`/categories/${selectedCategoryId}`);
//     setShowEditCategoryPopup(false);
//     fetchCategories();
//   };







//   const handleCategoryChange = (id) => {
//     const selected = categories.find((c) => c.id === parseInt(id));
//     setSelectedCategoryId(id);
//     setEditedName(selected.name);
//     setEditedType(selected.type);
//   };

//   // NEW: Create manual/external cash account
//   const handleCreateCashAccount = async () => {            // ← NEW
//     if (!cashAccountName.trim()) return alert("Account name is required");
//     setCreatingCash(true);
//     try {
//       await api.post("/external-accounts", {
//         businessId,
//         name: cashAccountName.trim(),
//         subtype: cashSubtype || "cash",
//         // server enforces: is_active=1, is_funding=0, is_manual=1
//       });
//       setShowAddCashPopup(false);
//       setCashAccountName("Cash");
//       setCashSubtype("cash");
//       await fetchAccounts();
//     } catch (err) {
//       console.error("Error creating cash account:", err);
//       alert("Failed to add cash account.");
//     } finally {
//       setCreatingCash(false);
//     }
//   };

//   return (
//     <div className="accounting-page-container">
//       <h4 style={{textAlign:'center'}}>Accounting Dashboard</h4>

//       <hr />
// <div className="pl-card">
//   <div className="pl-card-header">
//     <div className="pl-filters">
//       <label>
//         Start{"  "}
//         <input type="date" value={plStartDate} onChange={(e) => setPlStartDate(e.target.value)} />
//       </label>
//       <label>
//         End{"  "}
//         <input type="date" value={plEndDate} onChange={(e) => setPlEndDate(e.target.value)} />
//       </label>
//       <label>
//         Account{"  "}
//         <select value={plAccountId} onChange={(e) => setPlAccountId(e.target.value)}>
//           <option value="">All</option>
//           {accounts.map(a => (
//             <option key={a.account_id} value={a.account_id}>
//               {a.bank_name || a.name || "Account"} {a.mask ? `•${a.mask}` : ""}
//             </option>
//           ))}
//         </select>
//       </label>
//       <button className="ref-btn-act" onClick={fetchProfitLossSummary} disabled={loadingPie}>
//         {loadingPie ? "" : ""}
//         <FaSyncAlt />
//       </button>
//     </div>
//   </div>

//   {pieError && <div className="pl-error">{pieError}</div>}

//   <div className="pl-pies">
//     <div className="pl-pie">
//       <h6>Income</h6>
//       <div className="pl-pie-inner">
//         <ResponsiveContainer width="100%" height="100%">
//           <PieChart>
//             <Pie data={incomeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} isAnimationActive={false}>
//               {incomeData.map((entry, idx) => (
//                 <Cell key={`inc-${idx}`} fill={colorFor(entry.name, idx)} />
//               ))}
//             </Pie>
//             <Tooltip formatter={(v) => Number(v).toLocaleString("en-US",{style:"currency",currency:"USD"})} />
//             <Legend />
//           </PieChart>
//         </ResponsiveContainer>
//       </div>
//     </div>

//     <div className="pl-pie">
//   <h6>Expense</h6>
//   <div className="pl-pie-inner">
//     <ResponsiveContainer width="100%" height="100%">
//       <PieChart >
//         <Pie
//           data={expenseData}
//           dataKey="value"
//           nameKey="name"
//           cx="50%"
//           cy="50%"
//           outerRadius={100}
//           isAnimationActive={false}
//         >
//           {expenseData.map((entry, idx) => (
//             <Cell key={`exp-${idx}`} fill={colorFor(entry.name, idx)} />
//           ))}
//         </Pie>

//         <Tooltip
//           formatter={(v, name) => {
//             const pct = totalIncomeBase ? (Number(v) / totalIncomeBase) * 100 : 0;
//             return [
//               Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" }),
//               `${name} (${pct.toFixed(1)}% of Income)`
//             ];
//           }}
//         />

//         {/* Reserve space for legend so it doesn't overlap totals */}
//         <Legend layout="horizontal" verticalAlign="bottom" align="center" height={30} wrapperStyle={{ marginTop: -10 }}/>
//       </PieChart>
//     </ResponsiveContainer>
//   </div>
// </div>

//   </div>
 

// </div>


     

//       {showAddCategoryPopup && (
//         <div className="popup">
//           <h4>Add Category</h4>
//           <input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="Name" />
//           <select value={newCategoryType} onChange={(e) => setNewCategoryType(e.target.value)}>
//             <option value="">Select Type</option>
//             <option value="Income">Income</option>
//             <option value="Expense">Expense</option>
//           </select>
//           <button onClick={handleAddCategory}>Submit</button>
//           <button onClick={() => setShowAddCategoryPopup(false)}>Cancel</button>
//         </div>
//       )}

//       {showEditCategoryPopup && (
//         <div className="popup">
//           <h4>Edit Category</h4>
//           <select value={selectedCategoryId} onChange={(e) => handleCategoryChange(e.target.value)}>
//             <option value="">Select Category</option>
//             {categories.map((c) => (
//               <option key={c.id} value={c.id}>{c.name}</option>
//             ))}
//           </select>
//           {selectedCategoryId && (
//             <>
//               <input value={editedName} onChange={(e) => setEditedName(e.target.value)} />
//               <select value={editedType} onChange={(e) => setEditedType(e.target.value)}>
//                 <option value="income">Income</option>
//                 <option value="expense">Expense</option>
//               </select>
//               <button onClick={handleEditCategory}>Update</button>
//               <button onClick={handleDeleteCategory}>Delete</button>
//             </>
//           )}
//           <button onClick={() => setShowEditCategoryPopup(false)}>Cancel</button>
//         </div>
//       )}

//       {/* NEW: Add Cash Account Popup */}
//       {showAddCashPopup && (
//         <div className="popup">
//           <h4>Add Cash Account</h4>
//           <label style={{ display: "block", marginBottom: 8 }}>Account Name</label>
//           <input
//             value={cashAccountName}
//             onChange={(e) => setCashAccountName(e.target.value)}
//             placeholder="e.g., Cash, Petty Cash, External Checking"
//           />
//           <label style={{ display: "block", margin: "12px 0 8px" }}>Subtype</label>
//           <select value={cashSubtype} onChange={(e) => setCashSubtype(e.target.value)}>
//             <option value="cash">cash</option>
//             <option value="external">external</option>
//             <option value="savings">savings</option>
//             <option value="checking">checking</option>
//           </select>
//           <div style={{ marginTop: 12 }}>
//             <button disabled={creatingCash} onClick={handleCreateCashAccount}>
//               {creatingCash ? "Adding..." : "Add Account"}
//             </button>
//             <button onClick={() => setShowAddCashPopup(false)} style={{ marginLeft: 8 }}>
//               Cancel
//             </button>
//           </div>
//         </div>
//       )}

//       <hr className="hr-act" />
//       <div className="connect-btn-wrapper">
              
// <span
//   className="action-tag-transactions"
//   onClick={() => navigate(`/dashboard/business/${businessId}/banking`)}
// >
//   Add / Edit Bank Accounts
// </span>

               
//         <span
//           onClick={() => navigate(`/dashboard/business/${businessId}/transactions-view`)}
//           className="action-tag-transactions"
//         >
//           Transactions
//         </span>

//         <span
//           onClick={() => navigate(`/dashboard/business/${businessId}/payroll-reconciliation`)}
//           className="action-tag-transactions"
//         >
//           Payroll Reconciliation
//         </span>

        
//         <span
//           onClick={() => navigate(`/dashboard/business/${businessId}/cash-recon`)}
//           className="action-tag-transactions"
//         >
//           Cash-in Reconciliation
//         </span>
    

        
      
//             </div>
      
//     </div>
//   );
// };

// export default AccountingPage;

